/**
 * Roles Store - Role and Permission Management
 *
 * Manages role and permission state in the frontend
 * Provides CRUD operations for roles
 * Handles WebSocket real-time updates
 */

import { api } from "@/utils/api";
import type {
  CreateRoleInput,
  Permission,
  Role,
  RoleStats,
  RoleWithPermissions,
  UpdateRoleInput,
} from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useRolesStore = defineStore("roles", () => {
  // State
  const roles = ref<RoleWithPermissions[]>([]);
  const permissions = ref<Permission[]>([]);
  const stats = ref<RoleStats>({
    totalRoles: 0,
    totalPermissions: 0,
    rolesWithPermissions: 0,
    averagePermissionsPerRole: 0,
  });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const getRoleById = computed(() => {
    return (roleId: number) => roles.value.find((r) => r.id === roleId);
  });

  const getRoleByName = computed(() => {
    return (name: string) => roles.value.find((r) => r.name === name);
  });

  const adminRole = computed(() => getRoleByName.value("admin"));
  const userRole = computed(() => getRoleByName.value("user"));

  // Actions

  /**
   * Fetch all roles with permissions
   */
  async function fetchRoles(): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const data = await api.get<RoleWithPermissions[]>(
        "/api/roles/with-permissions",
      );
      roles.value = data;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to fetch roles";
      console.error("Error fetching roles:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch all permissions
   */
  async function fetchPermissions(): Promise<void> {
    try {
      const data = await api.get<Permission[]>("/api/roles/permissions/all");
      permissions.value = data;
    } catch (err) {
      console.error("Error fetching permissions:", err);
      throw err;
    }
  }

  /**
   * Fetch role statistics
   */
  async function fetchStats(): Promise<void> {
    try {
      const data = await api.get<RoleStats>("/api/roles/stats");
      stats.value = data;
    } catch (err) {
      console.error("Error fetching role stats:", err);
      throw err;
    }
  }

  /**
   * Create new role
   */
  async function createRole(input: CreateRoleInput): Promise<Role> {
    isLoading.value = true;
    error.value = null;

    try {
      const role = await api.post<Role>("/api/roles", input);

      // Refresh roles list
      await fetchRoles();

      return role;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to create role";
      console.error("Error creating role:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Update role
   */
  async function updateRole(
    roleId: number,
    input: UpdateRoleInput,
  ): Promise<Role> {
    isLoading.value = true;
    error.value = null;

    try {
      const role = await api.put<Role>(`/api/roles/${roleId}`, input);

      // Update local state
      const index = roles.value.findIndex((r) => r.id === roleId);
      if (index !== -1) {
        // Preserve permissions if not updated
        const permissions =
          input.permissionIds !== undefined
            ? roles.value[index].permissions
            : roles.value[index].permissions;

        roles.value[index] = {
          ...role,
          permissions,
        };
      }

      // Refresh to get updated permissions
      await fetchRoles();

      return role;
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to update role";
      console.error("Error updating role:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Delete role
   */
  async function deleteRole(roleId: number): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await api.delete(`/api/roles/${roleId}`);

      // Remove from local state
      roles.value = roles.value.filter((r) => r.id !== roleId);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to delete role";
      console.error("Error deleting role:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Clear error
   */
  function clearError(): void {
    error.value = null;
  }

  return {
    // State
    roles,
    permissions,
    stats,
    isLoading,
    error,

    // Getters
    getRoleById,
    getRoleByName,
    adminRole,
    userRole,

    // Actions
    fetchRoles,
    fetchPermissions,
    fetchStats,
    createRole,
    updateRole,
    deleteRole,
    clearError,
  };
});
